import React, { CSSProperties, FunctionComponent } from 'react';
import { Button, Card, Responsive } from 'rs-emd-ui-atoms';
import { ImageWidget, TextWidget } from '../..';
import { ITextEditor } from '../../../../models/campaign-pages/common.model';
import { IImageTextBoxWidget } from '../../../../models/campaign-pages/widgets/image-and-textbox/image-and-textbox.model';
import styles from './image-and-textbox-component.module.scss';

type ImageAndTextboxProps = {
  imageTextBox: IImageTextBoxWidget;
  /**Will only be considered if positioning of image is top/bottom */
  isContainerFullHeight?: boolean;
  isBtnFullWidth?: boolean;
  maxImgHeight?: number;
};

export const ImageAndTextbox: FunctionComponent<ImageAndTextboxProps> = (props: ImageAndTextboxProps) => {
  let ContainerTag = (props.imageTextBox.link?.URL ? 'a' : 'div') as keyof JSX.IntrinsicElements;
  let linkAttributes =
    ContainerTag === 'a' ? { href: props.imageTextBox.link?.URL ?? undefined, target: props.imageTextBox.link?.target ?? undefined } : {};

  //filter so as to exclude where there is no text and just styling
  let textWithData: ITextEditor[] = props.imageTextBox.text?.filter((text) => text.text) ?? [];

  let texts = textWithData.length !== 0 && (
    <div className={styles.text}>
      <TextWidget
        textWidget={{
          textData: textWithData.map((txt) => {
            return { ...txt, textSize: txt.textSize ?? 'sm' };
          }),
        }}
        isContainerFullHeight={false}></TextWidget>
    </div>
  );

  let button = (props.imageTextBox.button?.text || props.imageTextBox.button?.icon) && (
    <div className={styles['button-container']}>
      <Button
        {...linkAttributes}
        isExternalLink={true}
        text={
          props.imageTextBox.button.type !== 'buttonWithIcon' && props.imageTextBox.button.text ? props.imageTextBox.button.text : undefined
        }
        icon={
          props.imageTextBox.button.type !== 'buttonWithText' && props.imageTextBox.button.icon ? props.imageTextBox.button.icon : undefined
        }
        buttonType={props.imageTextBox.button?.style ?? undefined}
        size={props.imageTextBox.button.type === 'buttonWithIcon' ? 'small-icon' : 'default'}
        iconPosition={props.imageTextBox.button.type === 'buttonWithTextAndIconRight' ? 'right' : 'left'}
        className={`${styles.button} ${props.isBtnFullWidth ? styles['full-width-btn'] : ''}`}
      />
    </div>
  );

  //texts and button
  function content(textVertAlignment?: string, buttonVertAlignment?: string) {
    //show content container only if there is at least text or button to be displayed
    if ((textWithData && textWithData.length !== 0) || props.imageTextBox.button?.text || props.imageTextBox.button?.icon) {
      return (
        <div
          className={`${styles['content-container']}  
            ${
              props.imageTextBox.button?.text || props.imageTextBox.button?.icon
                ? textWithData.length !== 0
                  ? styles['content-vert-align-text-' + textVertAlignment + '-button-' + buttonVertAlignment]
                  : styles['button-vert-align-' + buttonVertAlignment]
                : styles['text-vert-align-' + textVertAlignment]
            }    ${
            props.imageTextBox.imageDesktopPosition === 'left' || props.imageTextBox.imageDesktopPosition === 'right'
              ? styles['left-right-contents-container']
              : styles['top-bottom-contents-container']
          } 
            ${
              props.imageTextBox.imageMobilePosition === 'left' || props.imageTextBox.imageMobilePosition === 'right'
                ? styles['mobile-left-right-contents-container']
                : styles['mobile-top-bottom-contents-container']
            }
              
          `}>
          {texts}
          {button}
        </div>
      );
    }
  }

  return (
    <ContainerTag
      {...linkAttributes}
      className={styles['image-and-textbox-card-container']}
      style={
        {
          '--desktop-container-height': props.isContainerFullHeight ? '100%' : 'auto',
          '--mobile-container-height':
            props.isContainerFullHeight && ['top', 'bottom'].includes(props.imageTextBox.imageMobilePosition ?? 'top') ? '100%' : 'auto',

          //image width percentage
          '--desktop-image-width-percentage':
            props.imageTextBox.image?.desktop?.width ??
            (['left', 'right'].includes(props.imageTextBox.imageDesktopPosition ?? 'top') ? '33%' : '100%'),
          '--tablet-image-width-percentage':
            props.imageTextBox.image?.tablet?.width ??
            (['left', 'right'].includes(props.imageTextBox.imageDesktopPosition ?? 'top') ? '33%' : '100%'),
          '--mobile-image-width-percentage':
            props.imageTextBox.image?.mobile?.width ??
            (['left', 'right'].includes(props.imageTextBox.imageMobilePosition ?? 'top') ? '33%' : '100%'),

          //button horizontal alignment
          '--desktop-button-horz-align':
            props.imageTextBox.buttonHorizontalAlignment?.desktop !== 'right'
              ? props.imageTextBox.buttonHorizontalAlignment?.desktop
              : 'flex-end',
          '--mobile-button-horz-align':
            props.imageTextBox.buttonHorizontalAlignment?.mobile !== 'right'
              ? props.imageTextBox.buttonHorizontalAlignment?.mobile
              : 'flex-end',

          //button vertical alignment
          '--desktop-button-vert-align':
            props.imageTextBox.buttonVerticalAlignment?.desktop !== 'bottom'
              ? props.imageTextBox.buttonVerticalAlignment?.desktop
              : 'flex-end',
          '--mobile-button-vert-align':
            props.imageTextBox.buttonVerticalAlignment?.mobile !== 'bottom'
              ? props.imageTextBox.buttonVerticalAlignment?.mobile
              : 'flex-end',
        } as CSSProperties
      }>
      <Card
        className={`${styles['image-and-textbox-card']} ${
          props.imageTextBox.imageDesktopPosition === 'top' || props.imageTextBox.imageDesktopPosition === 'bottom'
            ? styles['top-bottom-image-container']
            : ''
        } ${
          props.imageTextBox.imageMobilePosition === 'top' || props.imageTextBox.imageMobilePosition === 'bottom'
            ? styles['mobile-top-bottom-image-container']
            : ''
        }`}
        backgroundColor={props.imageTextBox.backgroundColour ?? ''}
        backgroundOpacity={props.imageTextBox.opacity ? parseInt(props.imageTextBox.opacity) : undefined}
        padding={true}
        radius={'0.8rem'}>
        <div
          className={`${styles['card-image-and-contents']} 
        ${props.imageTextBox.image?.desktop?.url ? styles['image-position-' + props.imageTextBox.imageDesktopPosition] : ''}  ${
            props.imageTextBox.image?.mobile?.url ? styles['mobile-image-position-' + props.imageTextBox.imageMobilePosition] : ''
          }
        `}>
          {/**Image*/}
          <ImageWidget
            isContainerFullHeight={false}
            image={{ image: props.imageTextBox.image, imageHorizontalAlignment: { desktop: 'center', mobile: 'center' } }}
            imageContainerClassName={`${styles['image-container']} ${
              props.imageTextBox.imageDesktopPosition === 'left' || props.imageTextBox.imageDesktopPosition === 'right'
                ? styles['left-right-image-container']
                : ''
            } ${
              props.imageTextBox.imageMobilePosition === 'left' || props.imageTextBox.imageMobilePosition === 'right'
                ? styles['mobile-left-right-image-container']
                : ''
            }`}
            imageWrapperClassName={styles.image}
            maxImgHeight={props.maxImgHeight}
          />

          {/**Content*/}
          <Responsive breakpoints={['tablet', 'desktop', 'lg-desktop', 'xl-desktop']}>
            {content(
              props.imageTextBox.textAlignment?.desktop ?? undefined,
              props.imageTextBox.buttonVerticalAlignment?.desktop ?? undefined
            )}
          </Responsive>
          <Responsive breakpoints={['mobile']}>
            {content(
              props.imageTextBox.textAlignment?.mobile ?? undefined,
              props.imageTextBox.buttonVerticalAlignment?.mobile ?? undefined
            )}
          </Responsive>
        </div>
      </Card>
    </ContainerTag>
  );
};

ImageAndTextbox.defaultProps = {
  isContainerFullHeight: true,
};

export default ImageAndTextbox;
